import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { Text, TextAs, TextSizes, Tooltip } from '@clockout-dev/design-elements';
import RecordFields from './RecordFields';
import { SYNC_HISTORY_COPY } from './syncHistory.constants';
import styles from './styles/sync-history.module.scss';

/**
 * The stored data behind one dimension of the active sync, under its row in
 * the Payroll Sync Status card. Renders the view model from
 * `buildStoredDimensionView` (syncStoredData.utils.js); `view.kind` picks the
 * body and every string arrives already built.
 *
 * @param {Object} props
 * @param {Object} props.view
 */
function SyncDimensionDetail({ view }) {
    return (
        <div className={styles.dimensionBody}>
            {view.errorMessage ? (
                <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.errorText}>
                    {view.errorMessage}
                </Text>
            ) : null}
            {view.kind === 'unassigned' ? <Note>{view.note}</Note> : null}
            {view.kind === 'record' ? <RecordDetail view={view} /> : null}
            {view.kind === 'stored' ? <StoredRecordsDetail view={view} /> : null}
            {view.kind === 'allocation' ? <AllocationDetail view={view} /> : null}
        </div>
    );
}

function Note({ children }) {
    return (
        <Text as={TextAs.P} size={TextSizes.EXTRA_SMALL} className={styles.note}>
            {children}
        </Text>
    );
}

/** The attribution rule as a hover hint, so it is one icon per dimension instead of a paragraph. */
function AttributionHint({ text }) {
    return (
        <Tooltip title={text} placement="left">
            <span className={styles.hint} role="img" aria-label={SYNC_HISTORY_COPY.ATTRIBUTION_HINT}>
                <InfoCircleOutlined />
            </span>
        </Tooltip>
    );
}

/** Identity / employment / income: one record per account, refreshed in place. */
function RecordDetail({ view }) {
    return (
        <div className={styles.detail}>
            <div className={styles.metaRow}>
                <span className={styles.metaLabelGroup}>
                    <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.metaLabel}>
                        {view.meta.label}
                    </Text>
                    <AttributionHint text={view.hint} />
                </span>
                <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.metaValue}>
                    {view.meta.value}
                </Text>
            </div>
            {view.note ? <Note>{view.note}</Note> : <RecordFields fields={view.fields} ariaLabel={view.fieldsLabel} />}
        </div>
    );
}

/** Paystubs / shifts: rows this sync first stored, and the newest few of them. */
function StoredRecordsDetail({ view }) {
    return (
        <div className={styles.detail}>
            <div className={styles.storedCountLine}>
                <Text as={TextAs.SPAN} size={TextSizes.SMALL} className={styles.storedCount}>
                    {view.countText}
                </Text>
                <AttributionHint text={view.hint} />
            </div>
            {view.note ? <Note>{view.note}</Note> : null}
            {view.sliceText ? <Note>{view.sliceText}</Note> : null}
            {view.records.length ? (
                <ul className={styles.recordList} aria-label={view.listLabel}>
                    {view.records.map((record, index) => (
                        <li key={index} className={styles.recordItem}>
                            <details className={styles.recordDetails}>
                                <summary className={styles.recordSummary}>
                                    <span className={styles.recordSummaryPrimary}>{record.summary}</span>
                                </summary>
                                <RecordFields fields={record.fields} />
                            </details>
                        </li>
                    ))}
                </ul>
            ) : null}
        </div>
    );
}

/** Direct deposit: from account settings, allocations listed one by one, the rest as fields. */
function AllocationDetail({ view }) {
    return (
        <div className={styles.detail}>
            <Note>{view.note}</Note>
            {view.emptyNote ? <Note>{view.emptyNote}</Note> : null}
            {view.allocations.length ? (
                <ul className={styles.recordList} aria-label="Direct deposit allocations">
                    {view.allocations.map((fields, index) => (
                        <li key={index} className={styles.recordItem}>
                            <RecordFields fields={fields} />
                        </li>
                    ))}
                </ul>
            ) : null}
            <RecordFields fields={view.fields} />
        </div>
    );
}

export default SyncDimensionDetail;
